import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Star, Heart, Minus, Plus, ChevronDown, ShieldCheck, Truck, Package } from "lucide-react";
import { toast } from "sonner";
import { API, img } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import SizeGuideModal from "@/components/SizeGuideModal";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fmt, addToCart, setDrawerOpen, wishlist, toggleWishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null); setSize(null); setQty(1); setActiveImg(0);
    axios.get(`${API}/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
    axios.get(`${API}/products`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  useSEO({
    title: product ? `${product.name} — Just Kords` : "Just Kords",
    description: product ? product.description : undefined,
    jsonLd: product ? {
      "@context": "https://schema.org", "@type": "Product",
      name: product.name, description: product.description,
      image: product.images, brand: { "@type": "Brand", name: "Just Kords" },
      ...(product.num_reviews > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.num_reviews } } : {}),
      offers: { "@type": "Offer", priceCurrency: "INR", price: product.price_inr, availability: "https://schema.org/InStock", url: `https://justkords.com/product/${product.slug}` },
    } : null,
  });

  const pairs = useMemo(() => related.filter((p) => p.slug !== slug && p.category === (product?.category)).slice(0, 2).length
    ? related.filter((p) => p.slug !== slug && p.category === product?.category).slice(0, 2)
    : related.filter((p) => p.slug !== slug).slice(0, 2), [related, slug, product]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-alabaster pt-28 text-center">
        <p className="font-serif text-4xl">This set has moved on.</p>
        <p className="text-sm text-mutedc">The piece you're looking for isn't here anymore.</p>
        <Link to="/shop" className="rounded-full bg-crimson px-7 py-3 text-sm font-semibold text-white">Back to the shop</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-alabaster pt-28 lg:pt-36">
        <div className="mx-auto grid max-w-7xl animate-pulse gap-10 px-4 pb-20 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="aspect-[4/5] bg-linec" />
          <div className="space-y-4"><div className="h-8 w-3/4 bg-linec" /><div className="h-5 w-1/3 bg-linec" /><div className="h-24 bg-linec" /></div>
        </div>
      </div>
    );
  }

  const variant = (product.sizes || []).find((v) => v.size === size);
  const wished = wishlist.includes(product.slug);
  const selPriceInr = size && variant && variant.price_inr != null ? variant.price_inr : product.price_inr;
  const selPriceUsd = size && variant && variant.price_usd != null ? variant.price_usd : product.price_usd;
  const selMrp = size && variant && variant.mrp_inr != null ? variant.mrp_inr : product.mrp_inr;
  const savePct = selMrp > selPriceInr ? Math.round((1 - selPriceInr / selMrp) * 100) : 0;

  const add = (goCheckout) => {
    if (!size) { toast.error("Pick a size first"); return; }
    addToCart(product, size, qty);
    if (goCheckout) navigate("/checkout");
    else { toast.success(`Added to bag — ${product.name} · ${size}`); setDrawerOpen(true); }
  };

  const album = [...(product.images || []), "/size-chart.png"];

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <Breadcrumb className="py-6" data-testid="pdp-breadcrumb">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbLink asChild><Link to="/shop">Shop</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator /><BreadcrumbItem><span className="text-crimson">{product.name}</span></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid gap-12 lg:grid-cols-2">
          <div data-testid="pdp-gallery">
            <div className="group relative aspect-[4/5] overflow-hidden bg-[#EFEAE3]">
              <img
                key={activeImg}
                src={activeImg === album.length - 1 ? album[activeImg] : img(album[activeImg], 1400)}
                alt={activeImg === album.length - 1 ? `${product.name} size chart` : product.name}
                className={activeImg === album.length - 1
                  ? "absolute inset-0 h-full w-full bg-white object-contain p-4"
                  : "absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"}
                data-testid="pdp-main-image"
              />
              {activeImg === album.length - 1 && (
                <span className="absolute left-4 top-4 bg-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">Size chart</span>
              )}
              {(activeImg !== album.length - 1 ? product.tags || [] : []).map((t) => (
                <span key={t} className="absolute left-4 top-4 bg-crimson px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">{t}</span>
              ))}
            </div>
            <div className="mt-3 flex gap-3">
              {album.map((im, i) => (
                <button
                  key={`${im}-${i}`} data-testid={`pdp-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-[3/4] w-20 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-crimson" : "border-transparent"}`}
                >
                  <img src={i === album.length - 1 ? im : img(im, 300)} alt={`${product.name} view ${i + 1}`} className="h-full w-full bg-white object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:pt-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-crimson">{product.category}{product.occasion ? ` · ${product.occasion}` : ""}</p>
            <h1 data-testid="pdp-title" className="mt-3 font-serif text-4xl font-light leading-tight sm:text-5xl">{product.name}</h1>

            {product.num_reviews > 0 && (
              <div className="mt-4 flex items-center gap-2" data-testid="pdp-rating">
                <span className="flex text-crimson">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={15} fill={i < Math.round(product.rating) ? "currentColor" : "none"} />)}
                </span>
                <span className="font-mono text-xs text-mutedc">{product.rating} · {product.num_reviews} reviews</span>
              </div>
            )}

            <div className="mt-5 flex items-baseline gap-3" data-testid="pdp-price">
              <span className="font-serif text-4xl">{fmt(selPriceInr, selPriceUsd)}</span>
              {savePct > 0 && (
                <>
                  <span className="text-base text-mutedc line-through">{fmt(selMrp)}</span>
                  <span className="bg-crimson-sub px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-crimson">Save {savePct}%</span>
                </>
              )}
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-mutedc">Inclusive of all taxes</p>

            <p className="mt-6 max-w-md text-sm leading-relaxed text-mutedc whitespace-pre-line" data-testid="pdp-description">{product.description}</p>

            <div className="mt-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-mutedc">Colour</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(product.colors || []).map((c) => (
                  <span key={c} className="border border-linec bg-white px-3 py-1.5 text-xs">{c}</span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-mutedc">Size</p>
              <SizeGuideModal>
                <button data-testid="pdp-size-guide-button" className="text-xs font-semibold uppercase tracking-widest text-crimson underline underline-offset-4">Size guide</button>
              </SizeGuideModal>
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="pdp-sizes">
              {(product.sizes || []).map((v) => (
                <button
                  key={v.size}
                  data-testid={`pdp-size-${v.size}`}
                  disabled={!v.stock}
                  onClick={() => { setSize(v.size); setQty(1); }}
                  className={`min-w-[52px] border px-4 py-2.5 text-sm transition-colors duration-200 ${
                    !v.stock ? "cursor-not-allowed border-linec text-linec line-through"
                      : size === v.size ? "border-crimson bg-crimson text-white" : "border-ink/30 bg-white hover:border-ink"
                  }`}
                >
                  {v.size}
                  {v.price_inr != null && product.price_inr != null && v.price_inr > product.price_inr && (
                    <span className="block font-mono text-[9px] opacity-70">+{Math.round(v.price_inr - product.price_inr)}</span>
                  )}
                </button>
              ))}
            </div>
            {variant && variant.stock > 0 && variant.stock <= 2 && (
              <p data-testid="pdp-low-stock" className="mt-2 font-mono text-[11px] uppercase tracking-widest text-crimson">Size {variant.size} — only {variant.stock} left</p>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-ink/30 bg-white">
                <button data-testid="pdp-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-3 transition-colors hover:text-crimson" aria-label="Decrease"><Minus size={15} /></button>
                <span data-testid="pdp-qty" className="w-9 text-center font-mono text-sm">{qty}</span>
                <button data-testid="pdp-qty-plus" onClick={() => setQty((q) => Math.min(5, q + 1))} className="px-3.5 py-3 transition-colors hover:text-crimson" aria-label="Increase"><Plus size={15} /></button>
              </div>
              <button
                data-testid="pdp-add-to-cart"
                onClick={() => add(false)}
                className="flex-1 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson sm:flex-none"
              >
                Add to Bag
              </button>
              <button
                data-testid="pdp-buy-now"
                onClick={() => add(true)}
                className="flex-1 rounded-full bg-crimson px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark sm:flex-none"
              >
                Buy Now
              </button>
              <button
                data-testid="pdp-wishlist"
                onClick={() => toggleWishlist(product.slug)}
                aria-label="Wishlist"
                className={`rounded-full border border-ink/30 p-3.5 transition-colors duration-200 ${wished ? "border-crimson text-crimson" : "hover:border-crimson hover:text-crimson"}`}
              >
                <Heart size={18} fill={wished ? "currentColor" : "none"} />
              </button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 border-y border-linec py-5 text-center" data-testid="pdp-trust-row">
              {[[Truck, "Free ship ₹2,999+"], [Package, "Ships in 24-48 hrs"], [ShieldCheck, "Secure Razorpay pay"]].map(([Icon, t]) => (
                <div key={t} className="flex flex-col items-center gap-1.5">
                  <Icon size={17} className="text-crimson" />
                  <span className="font-mono text-[9px] uppercase tracking-widest text-mutedc">{t}</span>
                </div>
              ))}
            </div>

            <Accordion type="single" collapsible className="mt-2" data-testid="pdp-accordions">
              {[
                ["Product Details", `${product.category} in ${product.fabric.toLowerCase()}. ${product.description}`],
                ["Fabric & Care", `${product.fabric}. Gentle machine wash cold or dry clean for satin styles. Dry in shade, warm iron on reverse.`],
                ["Shipping & Returns", "Dispatched in 24-48 hrs. Free India shipping above ₹2,999 (₹99 below), express delivery available at ₹199. Returns are not accepted — exchange only for manufacturing defects or wrong size received, with an uncut parcel-opening video, registered within 48 hrs on WhatsApp."],
              ].map(([t, body]) => (
                <AccordionItem key={t} value={t}>
                  <AccordionTrigger data-testid={`pdp-accordion-${t.toLowerCase().split(" ")[0]}`} className="font-mono text-xs uppercase tracking-[0.25em]">{t}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-mutedc">{body}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {pairs.length > 0 && (
          <section className="mt-24" data-testid="pdp-complete-look">
            <Reveal className="flex items-end justify-between">
              <h2 className="font-serif text-3xl font-light sm:text-4xl">Complete the look</h2>
              <Link to="/shop" className="text-xs font-semibold uppercase tracking-widest text-crimson underline underline-offset-4">View all</Link>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {pairs.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Sticky mobile add bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-linec bg-white/95 px-4 py-3 backdrop-blur-xl lg:hidden" data-testid="pdp-sticky-bar">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-mutedc">{product.name}</p>
          <p className="font-serif text-xl">{fmt(product.price_inr, product.price_usd)}</p>
        </div>
        <button onClick={() => add(false)} className="rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white">Add to Bag</button>
      </div>
    </div>
  );
}
